import { authErrorMessage } from "@/lib/authErrors"
import { APP_LOGO, APP_NAME, APP_TAGLINE } from "@/lib/brand"
import { lumi } from "@/lib/lumi"
import { useAuthModal } from "@/hooks/useAuthModal"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import ErrorDialog from "@/components/ErrorDialog"

type Mode = "signin" | "signup" | "reset"

export default function AuthPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isOpen, closeAuth } = useAuthModal()
  const [mode, setMode] = useState<Mode>("signin")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [otp, setOtp] = useState("")
  const [countdown, setCountdown] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (countdown <= 0) return
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [countdown])

  if (!isOpen) return null

  const dismiss = () => {
    closeAuth()
    if (location.pathname === "/auth") navigate("/", { replace: true })
  }

  const afterAuth = () => {
    const next = location.pathname === "/auth" ? "/" : `${location.pathname}${location.search || ""}`
    window.location.href = next
  }

  const sendOtp = async () => {
    if (!email) {
      setError("Please enter email")
      return
    }
    try {
      await lumi.auth.sendOtp({
        email,
        type: mode === "reset" ? "resetPassword" : "register",
      })
      setCountdown(60)
    } catch (err) {
      setError(authErrorMessage(err))
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError("")
    setLoading(true)
    try {
      if (mode === "signin") {
        await lumi.auth.signInWithPassword({ email, password })
        afterAuth()
        return
      }
      if (mode === "signup") {
        await lumi.auth.signUpWithPassword({ email, password, otp })
        await lumi.auth.signInWithPassword({ email, password })
        afterAuth()
        return
      }
      await lumi.auth.resetPassword({ email, newPassword: password, otp })
      await lumi.auth.signInWithPassword({ email, password })
      afterAuth()
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    setError("")
    setOtp("")
  }

  return (
    <div className="fixed inset-x-0 bottom-20 top-[4.5rem] z-40 flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-6 sm:items-center">
      <button
        type="button"
        aria-label="Close login"
        className="absolute inset-0"
        onClick={dismiss}
      />
      <form
        onSubmit={submit}
        className="relative z-10 w-full max-w-sm rounded-2xl border-2 border-[#00C805] bg-black px-5 py-6 shadow-[0_20px_80px_rgba(0,0,0,0.65)]"
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-4 top-4 text-sm text-[#6E6E6E]"
        >
          Close
        </button>
        <img
          src={APP_LOGO}
          alt={APP_NAME}
          className="mx-auto h-28 w-auto max-w-[260px] object-contain sm:h-32"
        />
        <div className="flex min-h-16 items-center justify-center px-2">
          <p className="text-center text-sm leading-6 text-[#A8A8A8]">
            {APP_TAGLINE}
          </p>
        </div>

        <div className="space-y-3">
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email"
            className="w-full rounded-xl border border-[#1F1F1F] bg-black px-4 py-3 text-sm text-white outline-none focus:border-[#00C805]"
          />

          {(mode === "signup" || mode === "reset") ? (
            <div className="flex gap-2">
              <input
                type="text"
                required
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                placeholder="Verification code"
                className="w-full rounded-xl border border-[#1F1F1F] bg-black px-4 py-3 text-sm text-white outline-none focus:border-[#00C805]"
              />
              <button
                type="button"
                onClick={sendOtp}
                disabled={countdown > 0}
                className="shrink-0 rounded-xl border border-[#1F1F1F] px-3 text-xs text-[#00C805] disabled:text-[#6E6E6E]"
              >
                {countdown > 0 ? `${countdown}s` : "Send"}
              </button>
            </div>
          ) : null}

          <input
            type="password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={mode === "reset" ? "New password" : "Password"}
            className="w-full rounded-xl border border-[#1F1F1F] bg-black px-4 py-3 text-sm text-white outline-none focus:border-[#00C805]"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-5 w-full rounded-xl bg-[#00C805] py-3 text-sm font-semibold text-black disabled:opacity-60"
        >
          {loading ? "Working..." : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Reset password"}
        </button>

        <button
          type="button"
          onClick={() => lumi.auth.signInWithOAuth({ provider: "google" }).then(() => {
            afterAuth()
          }).catch((err) => setError(authErrorMessage(err)))}
          className="mt-3 w-full rounded-xl border border-[#1F1F1F] bg-[#141414] py-3 text-sm text-white"
        >
          Continue with Google
        </button>

        <div className="mt-6 space-y-2 text-center text-sm text-[#A8A8A8]">
          {mode === "signin" ? (
            <>
              <button type="button" className="text-[#00C805]" onClick={() => switchMode("reset")}>
                Forgot password?
              </button>
              <p>
                No account?{" "}
                <button type="button" className="text-white" onClick={() => switchMode("signup")}>
                  Sign up
                </button>
              </p>
            </>
          ) : (
            <p>
              Have an account?{" "}
              <button type="button" className="text-white" onClick={() => switchMode("signin")}>
                Sign in
              </button>
            </p>
          )}
          <button type="button" className="text-xs text-[#6E6E6E]" onClick={dismiss}>
            Keep browsing
          </button>
        </div>
      </form>

      {error ? <ErrorDialog title="Authentication Error" message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}
